// features/workstations/knowledge/feed-section.js
// ═══════════════════════════════════════════════════════════
// Domain Intelligence Feed — V1.
//
// Renders today's briefing (already auto-generated by the
// briefing cron) as one feed item with the Practitioner Lens.
// V1.5 will:
//   - break briefings into multiple item-sized entries
//   - add the Active Connection Layer (matches against open
//     trades + canvas state, surfaces "this affects your X")
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { ShiftRead } from '../shared';

export default function FeedSection({ workstation }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workstation?.domain_key) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/workstations/briefing?domain=${encodeURIComponent(workstation.domain_key)}`, { cache: 'no-store' });
        if (cancelled) return;
        const data = res.ok ? await res.json() : { briefing: null };
        setBriefing(data.briefing || null);
      } catch (e) {
        console.warn('[knowledge/feed] briefing load failed:', e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workstation?.domain_key]);

  if (loading) return <div className="ws-loading">Loading feed…</div>;

  if (!briefing) {
    return (
      <div className="ws-feed-empty">
        <div className="ws-feed-empty-title">No feed items yet today</div>
        <div className="ws-feed-empty-body">
          The intelligence cron generates the day's briefing at 11:30 AM ET.
          Once it fires, you'll see it here as a feed item with practitioner
          lens. <strong>V1.5 will break this into smaller item-sized entries
          + add the Active Connection Layer</strong> — items that affect your
          open trades or canvas will surface with action buttons.
        </div>
      </div>
    );
  }

  // Render the briefing as a single feed item.
  const bullets = Array.isArray(briefing.bullets)
    ? briefing.bullets
    : safeParse(briefing.bullets);
  const dateLabel = formatBriefDate(briefing.briefing_date);
  const generated = relTime(briefing.generated_at);

  return (
    <div className="ws-feed-list">
      <FeedItem
        type="synthesis"
        tone="gold"
        title={`${dateLabel} — daily synthesis`}
        meta={`${workstation.domain_key} · ${generated} · ${briefing.source_count || '?'} sources`}
        body={briefing.synthesis}
        bullets={bullets}
      />
    </div>
  );
}

function FeedItem({ type, tone, title, meta, body, bullets }) {
  return (
    <article className={`ws-feed-item ws-top-accent-${tone}`}>
      <div className="ws-feed-item-header">
        <span className={`ws-pill ws-pill-${tone}`}>{type}</span>
        <span className="ws-feed-item-meta">{meta}</span>
      </div>
      <div className="ws-feed-item-title">{title}</div>
      <div className="ws-feed-item-body">{body}</div>
      {Array.isArray(bullets) && bullets.length > 0 && (
        <ul className="ws-feed-item-bullets">
          {bullets.map((b, i) => <BriefingBullet key={i} bullet={b} />)}
        </ul>
      )}
      <ShiftRead variant="gold">
        This synthesis is the highest-confidence read across today's
        market data. Reference items appear here in V1.5 with direct
        connections to your open trades.
      </ShiftRead>
    </article>
  );
}

// Commit 83 — render the briefing generator's structured bullet
// shape ({tone, bullet, ctx}) instead of JSON-stringifying it.
// `bullet` = the tactical action; `ctx` = the one-line reasoning.
// Tone colors the dot + action text; ctx sits beneath in muted text.
const BULLET_TONES = new Set(['gold', 'sage', 'teal', 'purple', 'terra', 'amber', 'steel']);

function BriefingBullet({ bullet }) {
  // Back-compat: plain string bullets still work.
  if (typeof bullet === 'string') return <li>{bullet}</li>;

  // Defensive: if someone passed us an opaque shape, fall back.
  if (!bullet || typeof bullet !== 'object') return null;

  const action = bullet.bullet || bullet.text || bullet.body || '';
  const ctx    = bullet.ctx || '';
  const tone   = BULLET_TONES.has(bullet.tone) ? bullet.tone : 'gold';

  if (!action && !ctx) return null;

  return (
    <li className={`ws-feed-bullet-item ws-feed-bullet-${tone}`}>
      {action && <div className={`ws-feed-bullet-action ws-tone-${tone}`}>{action}</div>}
      {ctx && <div className="ws-feed-bullet-ctx">{ctx}</div>}
    </li>
  );
}

function safeParse(s) {
  try {
    const parsed = JSON.parse(s);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function formatBriefDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d + 'T12:00:00Z');
    const today = new Date();
    if (dt.toDateString() === today.toDateString()) return 'Today';
    const y = new Date(); y.setDate(y.getDate() - 1);
    if (dt.toDateString() === y.toDateString()) return 'Yesterday';
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return d; }
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}
